import { LazyImage } from "@website/ui/lazy-image";
import { useListItem } from "@website/ui/list-item/list-item.hook";
import styles from "@website/ui/list-item/list-item.module.scss";
import { ListItemPropertiesType } from "@website/ui/list-item/list-item.types";
import { deepCompare } from "@website/utils/deep-compare";
import { FC, memo, useMemo } from "react";

const ListItem: FC<ListItemPropertiesType> = (properties) => {
  const {
    className,
    EndAction,
    EndIcon,
    image,
    Meta,
    overline,
    role,
    size = "medium",
    subtitle,
    StartAction,
    StartIcon,
    title,
    isCollapsed,
    disabled,
    "aria-disabled": ariaDisabled
  } = properties;
  const { rootProps } = useListItem(properties);

  const inside = (
    <>
      {image && (
        <div className="flex-[0_0_64px]">
          <LazyImage alt={typeof title === "string" ? title : ""} src={image} />
        </div>
      )}
      {StartAction && (
        <div className={styles["start-action"]}>{StartAction}</div>
      )}
      {StartIcon && <div className={styles["icon"]}>{StartIcon}</div>}
      <div
        className={`flex-1 whitespace-nowrap transition-opacity duration-300 ${
          isCollapsed ? "opacity-0" : "opacity-100 delay-200"
        }`}
      >
        {overline && <div className={styles["overline"]}>{overline}</div>}
        {title && (
          <div
            className={`${styles["title"]} ${
              disabled || ariaDisabled ? styles["disabled"] : ""
            }`}
          >
            {title}
          </div>
        )}
        {subtitle && (
          <div
            className={`${styles["subtitle"]} ${
              disabled || ariaDisabled ? styles["disabled"] : ""
            }`}
          >
            {subtitle}
          </div>
        )}
      </div>
      {Meta && <div className={styles["meta"]}>{Meta}</div>}
      {EndIcon && <div className={styles["icon"]}>{EndIcon}</div>}
      {EndAction && <div className={styles["end-action"]}>{EndAction}</div>}
    </>
  );

  const Tag = useMemo(
    () => (role === "link" ? "a" : role === "button" ? "button" : "div"),
    [role]
  );

  const roleProperties = useMemo(
    () =>
      role === "link"
        ? rootProps.link
        : role === "button"
          ? rootProps.button
          : rootProps.default,
    [role, rootProps]
  );

  return (
    <Tag
      aria-disabled={ariaDisabled}
      {...roleProperties}
      className={`${styles["list-item"]} ${styles[size]} ${className ?? ""} ${
        isCollapsed && image ? "px-2" : "px-6"
      }`}
    >
      {inside}
    </Tag>
  );
};

const ListItemMemoized = memo(ListItem, deepCompare);
export { ListItemMemoized as ListItem };
